<script setup>
import {computed, onMounted, onUnmounted, ref, nextTick, watch} from 'vue'
import { router, usePage } from "@inertiajs/vue3"
import Toast from '@/Components/Common/Toast.vue'
import axios from 'axios'
import HeaderV2 from "@/Components/Reels/HeaderV2.vue";
import Hero from "@/Components/Reels/Hero.vue";
import FilterBar from "@/Components/Reels/FilterBar.vue";
import MobileFilterBar from "@/Components/Reels/MobileFilterBar.vue";
import JoinModal from "@/Components/Common/JoinModal.vue";
import TopSponsorsModal from "@/Components/Reels/TopSponsorsModal.vue";
import VideosGrid from "@/Components/Reels/VideosGrid.vue";
import {getFathomPagePrefix, trackEvent, trackFathomEvent} from "@/Helpers/analytics";
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'
import "@/../assets/js/lti-analytics-0.2.0.min.js"
import {useJoinOptions} from "@/composables/useJoinOptions";
import JoinGoldModal from "@/Components/Common/JoinGoldModal.vue";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";
import SfwJoinModal from "@/Components/Common/SfwJoinModal.vue";
import StarPowerModal from "@/Components/Reels/StarPowerModal.vue";
import CreateCastingModal from "@/Components/Casting/CreateCastingModal.vue";
import CastingsList from "@/Components/Casting/CastingsList.vue";
import CastingSupportModal from "@/Components/Casting/CastingSupportModal.vue";
import BuyStarsModal from "@/Components/Reels/BuyStarsModal.vue";
import CreateCastingWizardModal from "@/Components/Casting/CreateCastingWizardModal.vue";
import CastingSignupModal from "@/Components/Casting/CastingSignupModal.vue";
import ProducerJoinModal from "@/Components/Common/ProducerJoinModal.vue";


const pageProps = defineProps({
    data: Array,
    tag: Object,
    related_tags: Array,
    pagination: Object,
    read_from_cache: Boolean,
    cache_key: String,
    timeoutSettings: Object,
    timeoutLimitReached: Boolean,
    likes: Array,
    globalStats: Object,
    userStats: Object,
    filtered_counts: Object,
    castingsSettings: Object,
    starPacks: Array,
})

const { clearJoinOptions } = useJoinOptions()

const loadMoreTrigger = ref(null)
const hasMorePages = ref(true)
const isLoadingMore = ref(false)
const castingModalRef = ref(null)
const rows = ref([])
const tagInfo = ref(pageProps.tag)
let observer = null
let currentPage = 1
const componentId = ref(Math.random().toString(36).substring(7)) // Unique ID for this component instance
const STORAGE_KEY = 'feeds_browse_state'
const CACHE_KEY_PREFIX = 'feeds_browse_cache_chunk_'
const STATE_EXPIRY = 5 * 60 * 1000 // 5 minutes
const CHUNK_SIZE = 50 // Items per chunk to avoid hitting storage limits
const joinDefaultSource = 'Auditions_Landing_Page'
const joinTopSponsorsSource = ref('')
const joinStickyBottomCTASource = ref('')
const joinCreateCastingSource = ref('')
const joinVoteCastingSource = ref('')
const joinCreateCastingDefaultSource = ref('Casting_List_Popup')
const joinSfwCreateCastingSource = ref('')
const totalResultsCount = ref(pageProps.pagination.total)
const lastPage = ref(pageProps.pagination.last_page)
const params = new URLSearchParams(window.location.search)
const currentView = ref('all')
const currentSort = ref('trending')
const isKebabOpen = ref(false)
const searchQuery = ref(params.get('search') || '')
const searchDebounceTimeout = ref(null)
const viewsWithSort = ['all', 'explore']

// Filtered counts from API when search is active
const filteredAllCount = ref(pageProps.filtered_counts?.all || null)
const filteredExploreCount = ref(pageProps.filtered_counts?.explore || null)
const filteredSponsoredCount = ref(pageProps.filtered_counts?.sponsored || null)
const filteredReleasedCount = ref(pageProps.filtered_counts?.released || null)
const filteredMineCount = ref(pageProps.filtered_counts?.mine || null)

// Helper function to clean up restoration flags
const cleanupRestorationFlags = () => {
    sessionStorage.removeItem('cameFromIndex')
    sessionStorage.removeItem('shouldRestoreBrowseState')
    sessionStorage.removeItem('backArrowNavTimestamp')

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🧹 Cleaned up restoration flags`)
    }
}

const saveBrowseState = (additionalData = {}) => {
    const browseState = {
        scrollY: window.scrollY,
        currentPage: currentPage,
        rowsCount: rows.value.length,
        view: currentView.value,
        sort: currentSort.value,
        search: searchQuery.value,
        tagSlug: getTagSlug(),
        timestamp: Date.now(),
        totalResults: totalResultsCount.value,
        lastPage: lastPage.value,
        ...additionalData
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(browseState))

    // Clear any existing cache chunks first
    clearCacheChunks()

    // Cache ALL data in chunks to avoid storage limits
    try {
        const totalItems = rows.value.length
        const totalChunks = Math.ceil(totalItems / CHUNK_SIZE)

        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 💾 Caching ${totalItems} items in ${totalChunks} chunks...`)
        }

        let successfulChunks = 0

        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE
            const end = Math.min(start + CHUNK_SIZE, totalItems)
            const chunk = rows.value.slice(start, end)

            // Cache data
            const cachedChunk = chunk.map(row => row)

            try {
                sessionStorage.setItem(
                    `${CACHE_KEY_PREFIX}${i}`,
                    JSON.stringify(cachedChunk)
                )
                successfulChunks++
            } catch (e) {
                console.warn(`[${componentId.value}] ⚠️ Failed to cache chunk ${i}:`, e.message)
                // If we hit quota, stop caching but don't fail completely
                break
            }
        }

        // Store metadata about chunks
        sessionStorage.setItem(
            `${CACHE_KEY_PREFIX}metadata`,
            JSON.stringify({
                totalChunks: successfulChunks,
                totalItems: successfulChunks * CHUNK_SIZE >= totalItems ? totalItems : successfulChunks * CHUNK_SIZE,
                chunkSize: CHUNK_SIZE
            })
        )

        if (isDebugMode.value) {
            console.log(`[${componentId.value}] ✅ Successfully cached ${successfulChunks}/${totalChunks} chunks (${successfulChunks * CHUNK_SIZE} items)`)
        }

    } catch (e) {
        console.error(`[${componentId.value}] ❌ Cache error:`, e)
        clearCacheChunks()
    }
}

const clearCacheChunks = () => {
    // Remove all cache chunks
    const keysToRemove = []
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
            keysToRemove.push(key)
        }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key))
}

const loadBrowseState = () => {
    try {
        const savedState = sessionStorage.getItem(STORAGE_KEY)

        if (!savedState) {
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ❌ No saved state found`)
            }
            return null
        }

        const state = JSON.parse(savedState)

        // Check if state is expired
        if (Date.now() - state.timestamp > STATE_EXPIRY) {
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ⏰ State expired, ignoring`)
            }
            sessionStorage.removeItem(STORAGE_KEY)
            clearCacheChunks()
            return null
        }

        // Verify tag matches
        const tagSlug = getTagSlug()
        if (state.tagSlug !== tagSlug) {
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ❌ Tag mismatch, ignoring state`)
            }
            sessionStorage.removeItem(STORAGE_KEY)
            clearCacheChunks()
            return null
        }

        // Restore view, sort, and search
        currentView.value = validateView(state.view)
        currentSort.value = validateSort(state.sort)
        searchQuery.value = state.search || ''

        // Restore total count
        if (state.totalResults) {
            totalResultsCount.value = state.totalResults
        }

        // Restore last page
        if (state.lastPage) {
            lastPage.value = state.lastPage
        }

        // Update URL to match restored state
        const urlParams = new URLSearchParams()
        if (viewsWithSort.includes(state.view)) {
            urlParams.set('sort', state.sort)
            urlParams.set('view', state.view)
        } else {
            urlParams.set('view', state.view)
        }
        if (state.search) {
            urlParams.set('search', state.search)
        }
        window.history.replaceState({}, '', `/feed/auditions?${urlParams.toString()}`)

        if (isDebugMode.value) {
            console.log(`[${componentId.value}] ✅ Restored view: ${currentView.value}, sort: ${currentSort.value}`)
        }

        // Try to load cached data from chunks
        try {
            const metadata = sessionStorage.getItem(`${CACHE_KEY_PREFIX}metadata`)
            if (metadata) {
                const { totalChunks } = JSON.parse(metadata)
                const cachedItems = []

                for (let i = 0; i < totalChunks; i++) {
                    const chunkData = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${i}`)
                    if (chunkData) {
                        cachedItems.push(...JSON.parse(chunkData))
                    }
                }

                if (cachedItems.length > 0) {
                    state.cachedItems = cachedItems
                    if (isDebugMode.value) {
                        console.log(`[${componentId.value}] ✅ Loaded ${cachedItems.length} cached items`)
                    }
                }
            }
        } catch (e) {
            console.warn(`[${componentId.value}] ⚠️ Could not load cached data:`, e)
            // Gracefully continue without cache
            clearCacheChunks()
        }

        return state
    } catch (error) {
        console.error(`[${componentId.value}] Failed to load state:`, error)
        sessionStorage.removeItem(STORAGE_KEY)
        clearCacheChunks()
        return null
    }
}

const setupIntersectionObserver = () => {
    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🔧 setupIntersectionObserver called`, {
            hasObserver: !!observer,
            hasTrigger: !!loadMoreTrigger.value,
            rowsCount: rows.value.length
        })
    }

    if (observer) {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] Disconnecting existing observer`)
        }
        observer.disconnect()
        observer = null
    }

    if (!loadMoreTrigger.value) {
        console.warn(`[${componentId.value}] ⚠️ loadMoreTrigger not ready, retrying in 100ms...`)
        setTimeout(setupIntersectionObserver, 100)
        return
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach(entry => {
                if (isDebugMode.value) {
                    console.log(`[${componentId.value}] 👀 Intersection entry:`, {
                        isIntersecting: entry.isIntersecting,
                        hasMore: hasMorePages.value,
                        isLoading: isLoadingMore.value
                    })
                }

                // Only trigger on entering viewport, not leaving
                if (entry.isIntersecting && hasMorePages.value && !isLoadingMore.value) {
                    if (isDebugMode.value) {
                        console.log(`[${componentId.value}] 🔄 Intersection triggered - loading more`)
                    }

                    // Clear any pending load
                    if (loadPostsTimeout) {
                        clearTimeout(loadPostsTimeout)
                    }

                    // Debounce to prevent double loading
                    loadPostsTimeout = setTimeout(() => {
                        loadPosts()
                    }, 100)
                }
            })
        },
        {
            root: null,
            rootMargin: '300px',
            threshold: 0
        }
    )

    observer.observe(loadMoreTrigger.value)

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] ✅ Observer attached successfully`)
    }

    // Manual check if trigger is already in viewport
    nextTick(() => {
        const rect = loadMoreTrigger.value.getBoundingClientRect()
        const isInViewport = rect.top < (window.innerHeight + 300)

        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 📏 Manual viewport check:`, {
                triggerTop: rect.top,
                windowHeight: window.innerHeight,
                threshold: window.innerHeight + 300,
                isInViewport,
                shouldLoad: isInViewport && hasMorePages.value && !isLoadingMore.value
            })
        }

        if (isInViewport && hasMorePages.value && !isLoadingMore.value) {
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] 🔄 Trigger already in viewport, loading immediately`)
            }
            loadPosts()
        }
    })
}

const cleanupObserver = () => {
    if (observer) {
        observer.disconnect()
        observer = null
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 🗑️ Observer cleaned up`)
        }
    }
}

// Watch for when data changes (component is being reused by Inertia)
watch(() => pageProps.data, (newData, oldData) => {
    // Skip if this is the same data
    if (newData === oldData) return

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 📊 Data prop changed - component reused by Inertia`, {
            newLength: newData?.length,
            oldLength: oldData?.length
        })
    }

    // Reset state
    rows.value = newData || []
    tagInfo.value = pageProps.tag
    currentPage = 1
    hasMorePages.value = true
    isLoadingMore.value = false

    // Scroll to top
    window.scrollTo(0, 0)

    // Reinitialize observer
    nextTick(() => {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] Reinitializing observer after data change`)
        }
        setupIntersectionObserver()
    })
}, { deep: false })

const { props } = usePage()
const isDebugMode = ref(params.get('debug') === '1' || import.meta.env.VITE_REELS_DEBUG === 'true')
const paginationData = ref([])
const requiresAgeVerification = ref(props.requiresAgeVerification)

// Flash message handling
const showToast = ref(false)
const toastText = ref('')
const flashMessage = computed(() => {
    if (props.flash?.message) return props.flash.message
    return null
})
const flashLog = computed(() => {
    if (props.flash?.error) return { type: 'error', message: props.flash.error }
    if (props.flash?.success) return { type: 'log', message: props.flash.success }
    if (props.flash?.warning) return { type: 'warn', message: props.flash.warning }
    if (props.flash?.info) return { type: 'info', message: props.flash.info }
    return null
})
const loading = ref(false)

let overflowCheckInterval = null
let loadPostsTimeout = null
let unsubscribeCreateCastingFromUrl = null
onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const viewFromUrlParams = urlParams.get('view')

    if (requiresAgeVerification.value) {
        if (viewFromUrlParams === 'castings') {
            // isCastingSignupModalOpen.value = true
            isProducerJoinModalOpen.value = true
            producerJoinModalContext.value = 'pitch'
            joinSfwCreateCastingSource.value = 'SFW_Casting_List_Popup'

            trackFathomEvent('SFW Castings Page Loaded')
            trackEvent('SFW Castings Page Loaded')
        } else {
            // isSfwJoinModalOpen.value = requiresAgeVerification.value
            isProducerJoinModalOpen.value = true
            joinSfwCreateCastingSource.value = 'SFW_Join_Modal'

            trackFathomEvent('SFW Auditions Landing Page Loaded')
            trackEvent('SFW Auditions Landing Page Loaded')
        }
    }

    if (params.get('trigger') === 'join') {
        // isStarPowerModalOpen.value = true
        isProducerJoinModalOpen.value = true
    }

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🎬 Component mounted`)
    }

    // READ FLAGS IMMEDIATELY - before any other operations
    const shouldRestore = sessionStorage.getItem('shouldRestoreBrowseState') === 'true'
    const cameFromIndex = sessionStorage.getItem('cameFromIndex') === 'true'
    const navTimestamp = sessionStorage.getItem('backArrowNavTimestamp')

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🔍 Initial flag check (IMMEDIATE):`, {
            shouldRestore,
            cameFromIndex,
            navTimestamp,
            timeSinceNav: navTimestamp ? Date.now() - parseInt(navTimestamp) : null
        })
    }

    document.body.classList.add('reels-tag-page')
    // Ensure scroll works when returning from FeedV2 (which locks body/html overflow)
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    // Attempt restoration if BOTH flags are present
    if (shouldRestore && cameFromIndex) {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] ✅ Both flags present - attempting restoration`)
        }

        const savedState = loadBrowseState()

        if (savedState) {
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] 🔄 Restoring browse state via back arrow`, {
                    restoredView: currentView.value,
                    restoredSort: currentSort.value,
                    savedPage: savedState.currentPage,
                    savedRowsCount: savedState.rowsCount,
                    hasCachedItems: !!savedState.cachedItems,
                    cachedCount: savedState.cachedItems?.length || 0
                })
            }

            // If we have cached data, use it for instant restoration
            if (savedState.cachedItems && savedState.cachedItems.length > 0) {
                if (isDebugMode.value) {
                    console.log(`[${componentId.value}] ⚡ Using ${savedState.cachedItems.length} cached items for instant restoration`)
                }

                rows.value = savedState.cachedItems
                currentPage = savedState.currentPage

                // Restore scroll immediately with cached data
                restoreScrollPosition(savedState.scrollY)

                // Check if we have all the data or need to load more
                if (savedState.cachedItems.length < savedState.rowsCount) {
                    if (isDebugMode.value) {
                        console.log(`[${componentId.value}] ⚠️ Cache incomplete (${savedState.cachedItems.length}/${savedState.rowsCount}), loading remaining items...`)
                    }

                    const perPage = pageProps.pagination?.per_page || 8
                    const cachedPages = Math.ceil(savedState.cachedItems.length / perPage)

                    // Load remaining pages in background
                    if (savedState.currentPage > cachedPages) {
                        loadRemainingPagesInBackground(cachedPages + 1, savedState.currentPage)
                    }
                } else {
                    if (isDebugMode.value) {
                        console.log(`[${componentId.value}] ✅ Full cache restored - no additional loading needed`)
                    }
                }

            } else {
                // No cached data, fall back to loading pages
                if (isDebugMode.value) {
                    console.log(`[${componentId.value}] No cached data, loading pages normally`)
                }

                rows.value = pageProps.data || []
                currentPage = savedState.currentPage

                if (rows.value.length >= savedState.rowsCount) {
                    restoreScrollPosition(savedState.scrollY)
                } else {
                    loadMissingPages(savedState.currentPage, savedState.scrollY)
                }
            }

            // Track restoration pageview
            trackEvent('Auditions Page Viewed', {
                sort: currentSort.value,
                view: currentView.value,
                restored: true
            })

            // ✅ Clean up flags AFTER successful restoration
            cleanupRestorationFlags()

            // Handle flash messages
            if (flashMessage.value) {
                console[flashLog.value.type](flashLog.value.message)
                toastText.value = flashMessage.value
                showToast.value = true

                setTimeout(() => {
                    showToast.value = false
                }, 5000)
            }

            if (isDebugMode.value) {
                console.log('Tag:', tagInfo.value)
            }

            return
        } else {
            console.log(`[${componentId.value}] ⚠️ loadBrowseState returned null, falling back to normal init`)
        }
    } else {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 📍 Normal navigation (flags missing):`, {
                shouldRestore,
                cameFromIndex,
                reason: !shouldRestore ? 'shouldRestore flag missing' : 'cameFromIndex flag missing'
            })
        }
    }

    // Clean up flags if we didn't restore
    cleanupRestorationFlags()

    // Normal initialization - use URL params
    currentView.value = validateView(viewFromUrlParams)
    currentSort.value = validateSort(urlParams.get('sort'))

    rows.value = pageProps.data || []

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] Normal init: ${rows.value.length} rows`)
    }

    if (currentView.value === 'castings') {
        loadCastings(true)
    } else {
        // Pre-fetch count so the badge is visible immediately
        axios.get('/feed/api/castings', { params: { page: 1 } })
            .then(r => { castingsTotal.value = r.data.pagination.total })
            .catch(() => {})

        nextTick(() => {
            setupIntersectionObserver()
        })
    }

    // Track normal pageview
    trackEvent('Auditions Page Viewed', {
        sort: currentSort.value,
        view: currentView.value,
        restored: false
    })

    if (flashMessage.value) {
        console[flashLog.value.type](flashLog.value.message)
        toastText.value = flashMessage.value
        showToast.value = true

        setTimeout(() => {
            showToast.value = false
        }, 5000)
    }

    if (isDebugMode.value) {
        console.log('Tag:', tagInfo.value)
    }

    // DEBUG: Check if something is changing overflow after mount (LIMITED DURATION)
    if (isDebugMode.value) {
        overflowCheckInterval = setInterval(() => {
            const bodyOverflow = document.body.style.overflow
            const htmlOverflow = document.documentElement.style.overflow

            if (bodyOverflow === 'hidden' || htmlOverflow === 'hidden') {
                console.warn(`[${componentId.value}] ⚠️ OVERFLOW CHANGED TO HIDDEN!`, {
                    body: bodyOverflow,
                    html: htmlOverflow
                })
                console.trace('Stack trace to find culprit:')

                // Force it back
                document.body.style.overflow = 'auto'
                document.documentElement.style.overflow = 'auto'
            }
        }, 100)

        // Stop overflow check after 5 seconds to prevent memory leak
        setTimeout(() => {
            if (overflowCheckInterval) {
                clearInterval(overflowCheckInterval)
                overflowCheckInterval = null
                console.log(`[${componentId.value}] ✅ Overflow check stopped`)
            }
        }, 5000)
    }
})

onUnmounted(() => {
    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 💀 Component unmounting`)
    }

    if (unsubscribeCreateCastingFromUrl) {
        unsubscribeCreateCastingFromUrl()
        unsubscribeCreateCastingFromUrl = null
    }

    if (loadPostsTimeout) {
        clearTimeout(loadPostsTimeout)
    }

    if (overflowCheckInterval) {
        clearInterval(overflowCheckInterval)
    }

    cleanupObserver()
    document.body.classList.remove('reels-tag-page')
})

const isUserLoggedIn = ref(props.auth && props.auth.user && props.auth.user.id !== null)
const userBalance = computed(() => Number(props.auth?.user?.wallet_balance))

// Begin V2 Changes
const sortOptions = {
    'trending': 'Trending',
    'closest_to_goal': 'Closest to Goal',
    'newest': 'Newest',
    'expired': 'Expired',
}
const isJoinModalOpen = ref(false)
const isSfwJoinModalOpen = ref(false)
const isStarPowerModalOpen = ref(false)
const producerJoinModalContext = ref('general')
const isProducerJoinModalOpen = ref(false)
const isCastingSignupModalOpen = ref(false)
const isGoldJoinModalOpen = ref(false)
const isBuyStarsModalOpen = ref(false)
const buyStarsSource = ref('')
const isTopSponsorsModalOpen = ref(false)
const isCreateCastingModalOpen = ref(false)
const isCastingLoading = ref(false)
const isCastingSubmitted = ref(false)
const castingError = ref('')
const castingFieldErrors = ref({})
const castingSimilarWarning = ref(null)

const handleBuyStars = () => {
    isCreateCastingModalOpen.value = false
    if (isUserLoggedIn.value) {
        buyStarsSource.value = 'create_casting'
        isBuyStarsModalOpen.value = true
    } else {
        // isStarPowerModalOpen.value = true
        isProducerJoinModalOpen.value = true
    }
}

// Support modal (free-choice mode, support_cost === -1)
const castingsListRef = ref(null)
const isSupportModalOpen = ref(false)
const supportingCasting = ref(null)
const supportModalLoading = ref(false)
const supportModalError = ref('')
const supportModalSuccess = ref(false)

const handleOpenSupportModal = (casting) => {
    supportingCasting.value = casting
    supportModalError.value = ''
    supportModalSuccess.value = false
    isSupportModalOpen.value = true
}

const handleSupportModalClose = () => {
    isSupportModalOpen.value = false
    supportingCasting.value = null
    supportModalError.value = ''
    supportModalSuccess.value = false
}

const handleSupportModalSubmit = async (amount) => {
    supportModalLoading.value = true
    supportModalError.value = ''
    try {
        const response = await axios.post(`/feed/api/castings/${supportingCasting.value.id}/support`, { stars: amount })
        supportModalSuccess.value = true
        castingsListRef.value?.markSupported(supportingCasting.value.id, response.data.stars_received)

        // Update user balance
        if (response.data.remaining_balance !== undefined) {
            handleBalanceUpdate(response.data.remaining_balance)
        }
    } catch (e) {
        supportModalError.value = e.response?.data?.message || 'Something went wrong. Please try again.'
    } finally {
        supportModalLoading.value = false
    }
}

const handleSupportModalResetSuccess = () => {
    supportModalSuccess.value = false
    supportModalError.value = ''
}
const isGoldUser = computed(() => props.auth && props.auth.is_gold)
const walletBalance = ref(Number(props.auth?.user?.wallet_balance) || 0)

const handleBuyStarPack = (pack) => {
    const returnUrl = window.location.pathname + window.location.search
    const source = buyStarsSource.value || 'tag_page'
    openStarPackPurchaseUrl(pack, source, returnUrl)
}

const validateView = (view) => {
    if (['all','explore','mine','released','sponsored','castings'].includes(view)) {
        return view
    }
    return 'all'
}

// Castings tab state
const castingsList = ref([])
const castingsPage = ref(1)
const castingsLastPage = ref(1)
const castingsTotal = ref(0)
const castingsLoading = ref(false)
const castingsSort = ref('trending')

const loadCastings = async (reset = false) => {
    if (castingsLoading.value) return
    if (reset) {
        castingsList.value = []
        castingsPage.value = 1
        castingsLastPage.value = 1
    }
    castingsLoading.value = true
    try {
        const params = { page: castingsPage.value, sort: castingsSort.value }
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim()
        const response = await axios.get('/feed/api/castings', { params })
        if (!Array.isArray(response.data?.data)) {
            console.error('Failed to load castings: unexpected response format', response.data)
            return
        }
        castingsList.value.push(...response.data.data)
        // On the first page, prepend the logged-in user's own draft castings
        if (castingsPage.value === 1 && Array.isArray(response.data?.ownDraftCastings) && response.data.ownDraftCastings.length) {
            castingsList.value.unshift(...response.data.ownDraftCastings)
        }
        castingsLastPage.value = response.data.meta.last_page
        castingsTotal.value = response.data.meta.total
    } catch (e) {
        console.error('Failed to load castings', e)
    } finally {
        castingsLoading.value = false
    }
}

const handleCastingsSort = (sort) => {
    castingsSort.value = sort
    loadCastings(true)
}

const loadMoreCastings = async () => {
    if (castingsPage.value >= castingsLastPage.value) return
    castingsPage.value++
    await loadCastings()
}

const handleBalanceUpdate = (newBalance) => {
    if (props.auth?.user) {
        props.auth.user.wallet_balance = newBalance
    }
}

const validateSort = (sort) => {
    if (['trending','closest_to_goal','newest','expired'].includes(sort)) {
        return sort
    }
    return 'trending'
}

const loadPosts = async (reset = false, specificPage = null) => {
    if (isLoadingMore.value && !specificPage) {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] Already loading, skipping...`)
        }
        return
    }

    if (reset) {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 🔄 Resetting posts (filter/sort change)`)
        }
        currentPage = 1
        rows.value = []
        hasMorePages.value = true
        isLoadingMore.value = false
        loading.value = false
        cleanupObserver()
    }

    isLoadingMore.value = true
    loading.value = true

    const pageToLoad = specificPage || (reset ? 1 : currentPage + 1)

    const params = {
        page: pageToLoad,
        sort: currentSort.value,
        view: currentView.value
    }

    if (searchQuery.value.trim()) {
        params.search = searchQuery.value.trim()
    }

    const tagSlug = getTagSlug()

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 📡 Fetching posts:`, {
            page: params.page,
            sort: params.sort,
            view: params.view,
            tagSlug,
            currentRowsCount: rows.value.length,
            specificPage
        })
    }

    try {
        const response = await axios.get(
            route((props.routePrefix || 'www') + '.reels.tag.next', {
                tag: tagSlug,
                ...params
            })
        )

        if (response.data.data && Array.isArray(response.data.data)) {
            // track search event along with response count
            if (params.search) {
                trackEvent('Auditions Search', { query: params.search, results: response.data.data.length })
                trackFathomEvent('Auditions Search')
            }

            const existingIds = new Set(rows.value.map(post => post.id))
            const newItems = response.data.data.filter(post => !existingIds.has(post.id))

            // update total count display
            if (params.page === 1 && response.data.pagination?.total > 0) {
                totalResultsCount.value = response.data.pagination?.total
            }

            // update last page
            if (response.data.pagination?.last_page > 0) {
                lastPage.value = response.data.pagination?.last_page
            }

            // update filtered counts when searching (only on page 1 or reset)
            if (params.page === 1 && response.data.filtered_counts) {
                filteredAllCount.value = response.data.filtered_counts.all ?? null
                filteredExploreCount.value = response.data.filtered_counts.explore ?? null
                filteredSponsoredCount.value = response.data.filtered_counts.sponsored ?? null
                filteredReleasedCount.value = response.data.filtered_counts.released ?? null
                filteredMineCount.value = response.data.filtered_counts.mine ?? null
            } else if (!searchQuery.value) {
                // Clear filtered counts when not searching
                filteredSponsoredCount.value = null
                filteredReleasedCount.value = null
                filteredMineCount.value = null
            }

            if (isDebugMode.value) {
                paginationData.value.push({
                    page: response.data.pagination?.current_page || params.page,
                    count: response.data.data.length,
                    new_items: newItems.length,
                    read_from_cache: response.data.read_from_cache,
                    cache_key: response.data.cache_key,
                })
            }

            rows.value.push(...newItems)

            // 🔥 FIXED: Update currentPage to track highest loaded page
            if (specificPage) {
                currentPage = Math.max(currentPage, specificPage)
            } else if (!reset) {
                currentPage++
            }

            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ✅ Posts loaded:`, {
                    newItems: newItems.length,
                    totalRows: rows.value.length,
                    currentPage: currentPage
                })
            }

            // const perPage = pageProps.pagination?.per_page || 8
            // if (response.data.data.length < perPage) {
            if (lastPage.value === currentPage) {
                hasMorePages.value = false
                cleanupObserver()
                if (isDebugMode.value) {
                    console.log(`[${componentId.value}] ❌ No more pages available`)
                }
            } else if (reset) {
                nextTick(() => {
                    setupIntersectionObserver()
                })
            }

            return newItems.length
        } else {
            if (params.search) {
                trackEvent('Auditions Search', { query: params.search, results: 0 })
                trackFathomEvent('Auditions Search')
            }

            console.error('Unexpected data format:', response.data)
            return 0
        }
    } catch (error) {
        if (params.search) {
            trackEvent('Auditions Search', { query: params.search, results: 0, error })
            trackFathomEvent('Auditions Search')
        }

        console.error('Error fetching posts:', error)
        if (reset) {
            nextTick(() => {
                setupIntersectionObserver()
            })
        }
        return 0
    } finally {
        isLoadingMore.value = false
        loading.value = false
    }
}

const handleSortV2 = (value) => {
    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🔄 Sort changed to:`, value)
    }

    currentSort.value = value
    const urlParams = new URLSearchParams()
    urlParams.set('sort', value)
    urlParams.set('view', currentView.value)
    if (searchQuery.value) {
        urlParams.set('search', searchQuery.value)
    }
    window.history.replaceState({}, '', `/feed/auditions?${urlParams.toString()}`)
    window.scrollTo(0, 0)
    loadPosts(true)
}

const handleView = (value) => {
    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🔄 View changed to:`, value)
    }

    currentView.value = value

    const urlParams = new URLSearchParams()
    if (viewsWithSort.includes(value)) {
        urlParams.set('sort', currentSort.value)
        urlParams.set('view', value)
    } else {
        urlParams.set('view', value)
    }
    if (searchQuery.value) {
        urlParams.set('search', searchQuery.value)
    }
    window.history.replaceState({}, '', `/feed/auditions?${urlParams.toString()}`)

    window.scrollTo(0, 0)

    if (value === 'castings') {
        loadCastings(true)
    } else {
        loadPosts(true)
    }
}

const feedUrl = (post) => {
    const routePrefix = props.routePrefix || 'www'
    const routeName = routePrefix + '.reels.feed'

    // set sorting for feed page
    let feedSort = currentSort.value
    if ( ! viewsWithSort.includes(currentView.value)) {
        feedSort = 'trending'
    }

    return route(routeName, { tag: getTagSlug(), post: post.slug, sort: feedSort })
}

const prefetchFeed = (post) => {
    if (!post?.slug) return
    router.prefetch(feedUrl(post), {}, { cacheFor: '60s' })
}

const goToFeed = (post, index) => {
    if (isDebugMode.value) {
        console.log('=== goToFeed ===', { post, index })
    }

    // Save current state
    saveBrowseState({ targetPostId: post.id })

    const tagSlug = getTagSlug()

    sessionStorage.setItem('cameFromIndex', 'true')
    sessionStorage.setItem('cameFromCategory', tagSlug)

    const routePrefix = props.routePrefix || 'www'
    const routeName = routePrefix + '.reels.feed'

    trackAuditionAnalyticsEvent('Audition Clicked', post);

    router.visit(feedUrl(post), {
        preserveScroll: false,
        preserveState: true,
    })
}

const getTagSlug = () => {
    let tagSlug = tagInfo?.value?.slug || ''
    if (!tagSlug) {
        const pathname = window.location.pathname
        if (pathname.includes('feed')) {
            tagSlug = pathname.split('/').pop()
        }
    }
    return tagSlug
}

const manualReattach = () => {
    console.log('🔧 Manual reattach triggered')
    hasMorePages.value = true
    setupIntersectionObserver()
}

const isRestoring = ref(false)
const loadMissingPages = async (targetPage, scrollY) => {
    const perPage = pageProps.pagination?.per_page || 8
    const currentPageFromRows = Math.ceil(rows.value.length / perPage)
    const startPage = rows.value.length === 0 ? 1 : currentPageFromRows + 1

    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 📚 Loading missing pages ${startPage}-${targetPage}...`)
    }

    isRestoring.value = true

    try {
        // Load pages sequentially
        for (let page = startPage; page <= targetPage; page++) {
            await loadPosts(false, page)
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ✅ Loaded page ${page}/${targetPage}`)
            }
        }

        currentPage = targetPage
        restoreScrollPosition(scrollY)
    } catch (error) {
        console.error(`[${componentId.value}] ❌ Failed to restore:`, error)
        restoreScrollPosition(scrollY)
    } finally {
        isRestoring.value = false
    }
}

const restoreScrollPosition = (scrollY) => {
    nextTick(() => {
        // Wait for DOM to be ready
        requestAnimationFrame(() => {
            window.scrollTo(0, scrollY)

            if (isDebugMode.value) {
                console.log(`[${componentId.value}] 📍 Scroll restored to ${scrollY}`)
            }

            // Reattach observer after scroll settles
            setTimeout(() => {
                setupIntersectionObserver()
            }, 100)
        })
    })
}

// New function for background loading that doesn't block UI
const loadRemainingPagesInBackground = async (startPage, endPage) => {
    if (isDebugMode.value) {
        console.log(`[${componentId.value}] 🔄 Background loading pages ${startPage}-${endPage}`)
    }

    try {
        // Load pages sequentially in background
        for (let page = startPage; page <= endPage; page++) {
            await loadPosts(false, page)
            if (isDebugMode.value) {
                console.log(`[${componentId.value}] ✅ Background loaded page ${page}/${endPage}`)
            }
        }

        currentPage = endPage

        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 🎉 Background loading complete`)
        }
    } catch (error) {
        console.error(`[${componentId.value}] ❌ Background loading failed:`, error)
    }
}

const handleJoinModalOpen = (source) => {
    // isJoinModalOpen.value = true
    // isStarPowerModalOpen.value = true
    isProducerJoinModalOpen.value = true
    if (source === 'top_sponsors') {
        joinTopSponsorsSource.value = 'Top_Sponsors_Popup'
    } else if (source === 'sticky_bottom_cta') {
        joinStickyBottomCTASource.value = 'Sticky_Bottom_CTA'
    } else if (source === 'Castings_List_Upvote' || source === 'Castings_List_Downvote') {
        joinVoteCastingSource.value = source
    }
}

const handleJoinModalClose = () => {
    // isJoinModalOpen.value = false
    // isStarPowerModalOpen.value = false
    isProducerJoinModalOpen.value = false
    joinTopSponsorsSource.value = ''
    joinStickyBottomCTASource.value = ''
    joinCreateCastingSource.value = ''
    joinVoteCastingSource.value = ''
    clearJoinOptions()
}

// Sticky CTA state
const showStickyBottom = ref(false)
const heroRef = ref(null)
let heroObserver = null

const setupHeroObserver = () => {
    if (isUserLoggedIn.value) return

    heroObserver = new IntersectionObserver(([entry]) => {
        showStickyBottom.value = !entry.isIntersecting
    }, {
        threshold: 0
    })

    if (heroRef.value) {
        heroObserver.observe(heroRef.value)
    }
}

const cleanupHeroObserver = () => {
    if (heroObserver) {
        heroObserver.disconnect()
        heroObserver = null
    }
}

watch(() => isUserLoggedIn, (newVal) => {
    if (newVal) {
        cleanupHeroObserver()
        showStickyBottom.value = false
    } else {
        setupHeroObserver()
    }
})

onMounted(() => {
    setupHeroObserver()
})

onUnmounted(() => {
    cleanupHeroObserver()
})

const globalTopSponsors = ref([])
const isLoadingTopSponsors = ref(false)
const handleOpenLeaderboard = () => {
    isTopSponsorsModalOpen.value = true
    isLoadingTopSponsors.value = true

    axios
        .get(route(props.routePrefix + '.reels.global-top-sponsors', {
            page: 1,
            per_page: 10,
        }))
        .then(function (response) {
            globalTopSponsors.value = response.data
        })
        .catch(error => {
            console.error(error)
        })
        .finally(() => {
            isLoadingTopSponsors.value = false
        })
}

const handleCastingClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Create_Casting_Modal_Opened')
    joinCreateCastingSource.value = 'Create_Casting_Popup'
    isCreateCastingModalOpen.value = true
}

const CREATE_QUERY_TRUTHY = new Set(['true', '1', 'yes'])

const shouldOpenCreateModalFromSearch = (search) => {
    const raw = search.startsWith('?') ? search.slice(1) : search
    const sp = new URLSearchParams(raw)
    if (sp.get('action') === 'create-scene') {
        return true
    }
    const value = sp.get('create')
    if (value === null) {
        return false
    }
    if (value === '') {
        return true
    }
    return CREATE_QUERY_TRUTHY.has(String(value).toLowerCase())
}

const tryOpenCreateCastingFromUrl = () => {
    if (!shouldOpenCreateModalFromSearch(window.location.search || '')) {
        return
    }
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Create_Casting_Modal_Opened_Url')
    joinCreateCastingSource.value = 'Create_Casting_Url'
    isCreateCastingModalOpen.value = true
}

onMounted(() => {
    tryOpenCreateCastingFromUrl()
    unsubscribeCreateCastingFromUrl = router.on('navigate', () => {
        nextTick(() => tryOpenCreateCastingFromUrl())
    })
})

// const handleCastingSubmit = async (formData) => {
//     if (!isUserLoggedIn.value) {
//         isCastingSignupModalOpen.value = true
//         return
//     }
//
//     isCastingLoading.value = true
//     castingError.value = ''
//     castingFieldErrors.value = {}
//     castingSimilarWarning.value = null
//
//     try {
//         const response = await axios.post(route(props.routePrefix + '.reels.casting.store'), {
//             title:                  formData.title || null,
//             performers:             formData.performers.map(p => p.id),
//             performer_description:  formData.performerDescription || null,
//             format:                 formData.format || null,
//             tags:                   formData.tags,
//             description:            formData.description || null,
//             setting:                formData.setting,
//             director_notes:         formData.directorNotes || null,
//             force:                  formData.force || false,
//         })
//
//         isCastingSubmitted.value = true
//
//         // Update user balance
//         if (response.data.remaining_balance !== undefined) {
//             handleBalanceUpdate(response.data.remaining_balance)
//         }
//
//         if (currentView.value === 'castings') {
//             loadCastings(true)
//         }
//
//         castingModalRef.value?.clearDraftCookie()
//     } catch (e) {
//         if (e.response?.status === 409 && e.response?.data?.similar) {
//             castingSimilarWarning.value = e.response.data.existing
//         } else if (e.response?.status === 422 && e.response?.data?.errors) {
//             // Laravel validation errors - extract field-specific errors
//             const errors = e.response.data.errors
//             castingFieldErrors.value = Object.keys(errors).reduce((acc, key) => {
//                 acc[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key]
//                 return acc
//             }, {})
//             // Set general error message if no field-specific errors
//             if (Object.keys(castingFieldErrors.value).length === 0) {
//                 castingError.value = e.response?.data?.message || 'Please fix the errors below.'
//             }
//         } else {
//             castingError.value = e.response?.data?.message || 'Something went wrong. Please try again.'
//         }
//     } finally {
//         isCastingLoading.value = false
//     }
// }

const handleStarPowerModalClose = (trigger) => {
    // isStarPowerModalOpen.value = false
    isProducerJoinModalOpen.value = false

    trackEvent('Main Page Star Power Modal Closed')
    trackFathomEvent('Main Page Star Power Modal Closed')
}

// const handleCastingSignupModalClose = (trigger) => {
//     isCastingSignupModalOpen.value = false
//     joinSfwCreateCastingSource.value = ''
//
//     trackEvent('Casting Signup Modal Closed')
//     trackFathomEvent('Casting Signup Modal Closed')
// }

const handleSearchFocus = () => {
    trackEvent('Auditions Search Focused')
    trackFathomEvent('Auditions Search Focused')
}

const handleSearchInput = (event) => {
    const value = event.target.value
    searchQuery.value = value

    if (searchDebounceTimeout.value) {
        clearTimeout(searchDebounceTimeout.value)
    }

    searchDebounceTimeout.value = setTimeout(() => {
        if (isDebugMode.value) {
            console.log(`[${componentId.value}] 🔍 Search query changed:`, searchQuery.value)
        }

        // Update URL with search parameter
        const urlParams = new URLSearchParams()
        if (viewsWithSort.includes(currentView.value)) {
            urlParams.set('sort', currentSort.value)
            urlParams.set('view', currentView.value)
        } else {
            urlParams.set('view', currentView.value)
        }
        if (searchQuery.value) {
            urlParams.set('search', searchQuery.value)
        }
        window.history.replaceState({}, '', `/feed/auditions?${urlParams.toString()}`)

        if (currentView.value === 'castings') {
            loadCastings(true)
        } else {
            loadPosts(true)
        }
    }, 500)
}

const clearSearch = () => {
    searchQuery.value = ''
    if (searchDebounceTimeout.value) {
        clearTimeout(searchDebounceTimeout.value)
    }

    // Update URL without search parameter
    const urlParams = new URLSearchParams()
    if (viewsWithSort.includes(currentView.value)) {
        urlParams.set('sort', currentSort.value)
        urlParams.set('view', currentView.value)
    } else {
        urlParams.set('view', currentView.value)
    }
    window.history.replaceState({}, '', `/feed/auditions?${urlParams.toString()}`)

    trackEvent('Auditions Search Cleared')
    trackFathomEvent('Auditions Search Cleared')
    if (currentView.value === 'castings') {
        loadCastings(true)
    } else {
        loadPosts(true)
    }
}

const handleMyNaClicked = () => {
    trackEvent('MyNA Clicked')
    trackFathomEvent('MyNA Clicked')

    if (!isUserLoggedIn.value) {
        // isStarPowerModalOpen.value = true;
        isProducerJoinModalOpen.value = true;
        return
    }

    router.visit(route('members2.profile.my', { slug: props?.auth?.user?.display_name || `Naughty${props?.auth?.user?.id}` }), {
        preserveScroll: false,
        preserveState: true,
    })
}

const handleCastingsPageClick = () => {
    if (!isUserLoggedIn.value) {
        // isStarPowerModalOpen.value = true
        isProducerJoinModalOpen.value = true
        return
    }

    router.visit(route('members2.profile.my.castings', { slug: props?.auth?.user?.display_name || `Naughty${props?.auth?.user?.id}` }), {
        preserveScroll: false,
        preserveState: true,
    })
}

const computedCastingTotal = computed(() => {
    // When on castings view, show the filtered/loaded count (if loaded)
    // Otherwise show the global count to prevent pill from disappearing during AJAX load
    if (currentView.value === 'castings' && castingsTotal.value > 0) {
        return Number(castingsTotal.value)
    }
    return Number(pageProps.globalStats?.castings ?? 0)
})
const handleProducerJoinModalClose = () => {
    isProducerJoinModalOpen.value = false
    joinTopSponsorsSource.value = ''
    joinStickyBottomCTASource.value = ''
    joinCreateCastingSource.value = ''
    joinVoteCastingSource.value = ''
    joinSfwCreateCastingSource.value = ''
    clearJoinOptions()
}
</script>

<template>
    <!-- Debug info -->
    <div v-if="isDebugMode" class="fixed top-0 left-0 bg-black text-white p-2 z-[51] text-xs max-w-[475px] max-h-[250px] overflow-y-auto">
        Results Length: {{ rows.length }}<br>
        Current Page: {{ currentPage }}<br>
        Has More: {{ hasMorePages }}<br>
        Is Loading: {{ isLoadingMore }}<br>
        Observer Active: {{ !!observer }}<br>
        Trigger Exists: {{ !!loadMoreTrigger }}<br>
        Tag: {{ tagInfo?.name || 'N/A' }}<br>
        <button
            @click="manualReattach"
            class="bg-green-500 px-2 py-1 mt-2 rounded text-white hover:bg-green-600"
        >
            Force Reattach Observer
        </button>
        <br>
        <button
            @click="console.log('Current state:', { rows: rows.length, observer: !!observer, trigger: !!loadMoreTrigger, hasMore: hasMorePages })"
            class="bg-blue-500 px-2 py-1 mt-2 rounded text-white hover:bg-blue-600"
        >
            Log Current State
        </button>
        <br>
        <button
            @click="console.log('SessionStorage:', {
                shouldRestore: sessionStorage.getItem('shouldRestoreBrowseState'),
                cameFromIndex: sessionStorage.getItem('cameFromIndex'),
                savedState: sessionStorage.getItem(STORAGE_KEY),
                cameFromCategory: sessionStorage.getItem('cameFromCategory')
            })"
            class="bg-purple-500 px-2 py-1 mt-2 rounded text-white hover:bg-purple-600"
        >
            Log SessionStorage
        </button>
    </div>

    <div class="main-container">
        <div class="min-h-screen bg-gray-100 pb-[70px] dark:bg-black">
            <HeaderV2 :isUserLoggedIn="isUserLoggedIn || false"
                      :isGoldUser="isGoldUser"
                      :routePrefix="props.routePrefix"
                      :requiresAgeVerification="requiresAgeVerification"
                      @joinClick="isProducerJoinModalOpen = true"
                      @buyStarsClick="buyStarsSource = 'menu'; isBuyStarsModalOpen = true"
                      @goldJoinClick="buyStarsSource = 'menu'; isBuyStarsModalOpen = true"
                      @update:kebabOpen="isKebabOpen = $event"
            />

            <div :class="['transition-[filter] duration-200', { 'blur-md': isKebabOpen }]">
                <div ref="heroRef">
                    <Hero :view="currentView"
                          :isUserLoggedIn="isUserLoggedIn || false"
                          :sponsoredThisMonth="Number(globalStats.sponsored_this_month)"
                          :sponsoredLast30Days="Number(globalStats.sponsored_last_30_days)"
                          :totalSponsored="Number(globalStats.sponsored ?? 0)"
                          :sponsoredTotalStarsGiven="Number(globalStats.sponsored_total_stars_given ?? 0)"
                          :totalStarsGiven="Number(globalStats.total_stars_given)"
                          :userSponsoredCount="Number(userStats.sponsored)"
                          :releasedCount="Number(globalStats.released)"
                          :userTotalStarsGiven="Number(userStats.total_stars)"
                          :topSponsors="globalStats.top_sponsors"
                          :searchQuery="searchQuery"
                          :routePrefix="props.routePrefix"
                          @joinClick="isProducerJoinModalOpen = true"
                          @openLeaderboard="handleOpenLeaderboard"
                          @myNaClicked="handleMyNaClicked"
                          @castingClick="handleCastingClick"
                          @castingsPageClick="handleCastingsPageClick"
                          @searchInput="handleSearchInput"
                          @clearSearch="clearSearch"
                          @searchFocus="handleSearchFocus"
                    />
                </div>

                <FilterBar :currentView="currentView"
                           :currentSort="currentSort"
                           :isUserLoggedIn="isUserLoggedIn"
                           :userSponsoredCount="Number(userStats.sponsored)"
                           :releasedCount="Number(globalStats.released)"
                           :sponsoredCount="Number(globalStats.sponsored ?? 0)"
                           :allCount="Number(globalStats.all)"
                           :exploreCount="Number(globalStats.explore)"
                           :totalCount="Number(totalResultsCount)"
                           :sortOptions="sortOptions"
                           :searchQuery="searchQuery"
                           :filteredSponsoredCount="Number(filteredSponsoredCount)"
                           :filteredReleasedCount="Number(filteredReleasedCount)"
                           :filteredMineCount="Number(filteredMineCount)"
                           :filteredAllCount="Number(filteredAllCount)"
                           :filteredExploreCount="Number(filteredExploreCount)"
                           :castingsCount="computedCastingTotal"
                           @updateCurrentSort="handleSortV2"
                           @updateCurrentView="handleView"
                />

                <MobileFilterBar :currentView="currentView"
                                 :currentSort="currentSort"
                                 :isUserLoggedIn="isUserLoggedIn"
                                 :userSponsoredCount="Number(userStats.sponsored)"
                                 :releasedCount="Number(globalStats.released)"
                                 :sponsoredCount="Number(globalStats.sponsored ?? 0)"
                                 :allCount="Number(globalStats.all)"
                                 :exploreCount="Number(globalStats.explore)"
                                 :totalCount="Number(totalResultsCount)"
                                 :sortOptions="sortOptions"
                                 :searchQuery="searchQuery"
                                 :filteredSponsoredCount="Number(filteredSponsoredCount)"
                                 :filteredReleasedCount="Number(filteredReleasedCount)"
                                 :filteredMineCount="Number(filteredMineCount)"
                                 :filteredAllCount="Number(filteredAllCount)"
                                 :filteredExploreCount="Number(filteredExploreCount)"
                                 :castingsCount="computedCastingTotal"
                                 @updateCurrentSort="handleSortV2"
                                 @updateCurrentView="handleView"
                                 @searchInput="handleSearchInput"
                                 @clearSearch="clearSearch"
                                 @searchFocus="handleSearchFocus" />

                <div>
                    <CastingsList
                        v-if="currentView === 'castings'"
                        ref="castingsListRef"
                        :castings="castingsList"
                        :loading="castingsLoading"
                        :hasMore="castingsPage < castingsLastPage"
                        :isUserLoggedIn="isUserLoggedIn"
                        :routePrefix="props.routePrefix || 'members2'"
                        :userBalance="userBalance"
                        :sort="castingsSort"
                        :supportCost="Number(pageProps.castingsSettings?.support_cost ?? 1)"
                        @loadMore="loadMoreCastings"
                        @castingClick="handleCastingClick"
                        @joinClick="handleJoinModalOpen"
                        @updateSort="handleCastingsSort"
                        @openSupportModal="handleOpenSupportModal"
                        @updateBalance="handleBalanceUpdate"
                    />

                    <VideosGrid
                        v-else
                        :posts="rows"
                        :view="currentView"
                        :isUserLoggedIn="isUserLoggedIn"
                        :loadingPosts="loading"
                        :routePrefix="props.routePrefix"
                        :requiresAgeVerification="requiresAgeVerification"
                        :useMyNaPromo="'card'"
                        :searchQuery="searchQuery"
                        @goToFeed="goToFeed"
                        @prefetchFeed="prefetchFeed"
                        @updateCurrentView="handleView"
                        @myNaClicked="handleMyNaClicked"
                        @castingClick="handleCastingClick"
                        @castingsPageClick="handleCastingsPageClick"
                    />

                    <div
                        ref="loadMoreTrigger"
                        class="h-20 w-full flex items-center justify-center"
                        v-show="currentView !== 'castings' && hasMorePages && rows.length > 0"
                    ></div>

                    <div v-if="isRestoring" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div class="text-white">Restoring your position...</div>
                    </div>
                </div>

                <hr class="border border-[#363636]">

                <!-- Bottom Sticky CTA - Shows when Hero is scrolled out of view -->
                <div
                    v-if="!isUserLoggedIn && showStickyBottom"
                    class="lg:hidden fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 z-20"
                >
                    <div class="p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                        <div class="text-center mb-2">
                            <p class="text-white/70 text-xs">
                                Full library + monthly Stars to sponsor
                            </p>
                        </div>
                        <button
                            @click="handleJoinModalOpen('sticky_bottom_cta')"
                            class="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold transition-all shadow-lg shadow-blue-600/25"
                        >
                            Get Your Stars
                        </button>
                    </div>
                </div>

                <!-- Footer -->
                <footer class="mt-8 md:mt-16">
                    <div class="container mx-auto px-4 py-8 md:py-12">
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                            <!-- About Section -->
                            <div v-if="!isUserLoggedIn">
                                <h3 class="text-base md:text-lg text-gray-300 font-semibold mb-3 md:mb-4 uppercase">WHO IS NAUGHTY AMERICA</h3>
                                <p class="text-gray-300 text-xs md:text-sm leading-relaxed mb-3 md:mb-4">
                                    With a history of over 25 years, Naughty America is recognized as one of the most award-winning adult entertainment platforms online. Very few studios have stood the test of time and thrived in the modern age of porn like NaughtyAmerica.com has.
                                </p>
                            </div>

                            <!-- Why Join Section -->
                            <div v-if="!isUserLoggedIn">
                                <h3 class="text-base md:text-lg text-gray-300 font-semibold mb-3 md:mb-4">WHY SHOULD I JOIN?</h3>
                                <div class="text-gray-300 text-xs md:text-sm leading-relaxed mb-3 md:mb-4">
                                    <p class="mb-3 md:mb-4">
                                        Joining Naughty America gets you access to thousands of the best content online. With our library that spans over 20 years of carefully curated content.
                                    </p>
                                </div>
                            </div>

                            <!-- Explore Section -->
                            <div v-if="!isUserLoggedIn">
                                <h3 class="text-base md:text-lg text-gray-300 font-semibold mb-3 md:mb-4">EXPLORE</h3>
                                <div class="grid grid-cols-2 gap-2 text-xs md:text-sm">
                                    <div class="space-y-1 md:space-y-2">
                                        <a :href="route('www.main.home')" class="block text-[#575757] hover:text-white">Home</a>
                                        <a :href="route('members2.auth.login')" class="block text-[#575757] hover:text-white">Login</a>
                                        <a :href="route('www.join.signUp')" class="block text-[#575757] hover:text-white">Join</a>
                                        <a :href="route('www.scene.index')" class="block text-[#575757] hover:text-white">Videos</a>
                                        <a :href="route('www.main.reDirectPerformer', 'vr-porn')" class="block text-[#575757] hover:text-white">VR Videos</a>
                                        <a :href="route('www.fantasy.index')" class="block text-[#575757] hover:text-white">Categories</a>
                                    </div>
                                    <div class="space-y-1 md:space-y-2">
                                        <a :href="route('www.site.index')" class="block text-[#575757] hover:text-white">Sites</a>
                                        <a :href="route('www.performer.index', 'top-rated')" class="block text-[#575757] hover:text-white">Pornstars</a>
                                        <a href="https://www.naughtyamerica.com/business" class="block text-[#575757] hover:text-white">Let's Do Business</a>
                                        <a href="https://www.naughtyrevenue.com" class="block text-[#575757] hover:text-white">Affiliates</a>
                                        <a :href="route('www.sitemap.sitemapHTML')" class="block text-[#575757] hover:text-white">Site Map</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    </div>

    <ProducerJoinModal :isOpen="isProducerJoinModalOpen"
                       :context="producerJoinModalContext"
                       :source="joinTopSponsorsSource || joinStickyBottomCTASource || joinCreateCastingSource || joinVoteCastingSource || joinSfwCreateCastingSource || joinDefaultSource"
                       :sfwTrigger="requiresAgeVerification"
                       :ageVerifyUrl="route('na.ageVerify.main.home')"
                       @close="handleProducerJoinModalClose"
    />

    <!--<JoinModal
        :isOpen="isJoinModalOpen"
        :source="joinTopSponsorsSource || joinStickyBottomCTASource || joinCreateCastingSource || joinDefaultSource"
        @close="handleJoinModalClose"
    />-->

    <JoinGoldModal :isOpen="isGoldJoinModalOpen"
                   :source="'Auditions_Landing_Page_Kebab_Menu'"
                   @close="isGoldJoinModalOpen = false"
    />

    <BuyStarsModal
        :isOpen="isBuyStarsModalOpen"
        :userBalance="walletBalance"
        :starPacks="pageProps.starPacks"
        @close="isBuyStarsModalOpen = false"
        @buy="handleBuyStarPack"
    />

    <TopSponsorsModal
        :isOpen="isTopSponsorsModalOpen"
        :isUserLoggedIn="isUserLoggedIn"
        :isInitialLoading="isLoadingTopSponsors"
        :leaderboard="globalTopSponsors"
        :userStats="userStats"
        :routePrefix="props.routePrefix"
        @close="isTopSponsorsModalOpen = false"
        @joinClick="handleJoinModalOpen('top_sponsors')"
        @viewMine="handleView('mine')"
    />

    <!--<SfwJoinModal :isOpen="isSfwJoinModalOpen" @close="isSfwJoinModalOpen = false" />

    <StarPowerModal :isOpen="isStarPowerModalOpen"
                    :source="joinTopSponsorsSource || joinStickyBottomCTASource || joinCreateCastingSource || joinVoteCastingSource || joinDefaultSource"
                    @close="handleStarPowerModalClose"
    />

    <CastingSignupModal :isOpen="isCastingSignupModalOpen"
                        :source="joinSfwCreateCastingSource || joinCreateCastingDefaultSource"
                        :sfwTrigger="requiresAgeVerification"
                        @close="handleCastingSignupModalClose"
    />-->

    <!--<CreateCastingModal
        ref="castingModalRef"
        :isOpen="isCreateCastingModalOpen"
        :userBalance="userBalance"
        :castingCost="Number(pageProps.castingsSettings?.default_star_cost ?? 5)"
        :loading="isCastingLoading"
        :errorMessage="castingError"
        :fieldErrors="castingFieldErrors"
        :submitted="isCastingSubmitted"
        :similarCasting="castingSimilarWarning"
        :isUserLoggedIn="isUserLoggedIn"
        @close="isCreateCastingModalOpen = false; isCastingSubmitted = false; castingError = ''; castingFieldErrors = {}; castingSimilarWarning = null"
        @dismissSimilar="castingSimilarWarning = null"
        @submit="handleCastingSubmit"
        @buyStars="handleBuyStars"
    />-->

    <!--<CreateCastingWizardModal
        ref="castingModalRef"
        :isOpen="isCreateCastingModalOpen"
        :userBalance="userBalance"
        :castingCost="Number(pageProps.castingsSettings?.default_star_cost ?? 5)"
        :loading="isCastingLoading"
        :errorMessage="castingError"
        :fieldErrors="castingFieldErrors"
        :submitted="isCastingSubmitted"
        :similarCasting="castingSimilarWarning"
        :isUserLoggedIn="isUserLoggedIn"
        @close="isCreateCastingModalOpen = false; isCastingSubmitted = false; castingError = ''; castingFieldErrors = {}; castingSimilarWarning = null"
        @dismissSimilar="castingSimilarWarning = null"
        @submit="handleCastingSubmit"
        @buyStars="handleBuyStars"
    />-->

    <BuyStarsModal
        :isOpen="isBuyStarsModalOpen"
        :userBalance="userBalance"
        :starPacks="pageProps.starPacks"
        @close="isBuyStarsModalOpen = false"
        @buy="handleBuyStarPack"
    />

    <CastingSupportModal
        :isOpen="isSupportModalOpen"
        :casting="supportingCasting"
        :availableStars="userBalance"
        :loading="supportModalLoading"
        :error="supportModalError"
        :success="supportModalSuccess"
        @close="handleSupportModalClose"
        @submit="handleSupportModalSubmit"
        @resetSuccess="handleSupportModalResetSuccess"
    />

    <Toast
        :show="showToast"
        position="bottomLeft"
        transitionName="fade"
        :transitionEnter="300"
        :transitionLeave="500"
        :text="toastText"
    />
</template>

<style>
.iub__us-widget {
    display: none;
    position: absolute;
    right: 0;
    border: 0;
}
.spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation-name: fadeIn, spin;
    animation-duration: 1s, 2s;
    animation-timing-function: ease-in;
    animation-iteration-count: 1, infinite;
    animation-fill-mode: forwards;
}
div[drawer-backdrop]:nth-last-child(1) {
    display: block;
    background: rgba(0, 0, 0, .8);
}

.bg-header {
    background-color: #141414;
}

@keyframes fadeIn {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

@keyframes spin {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

.loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.container {
    max-width: 99% !important;
}

.loading2 {
    transform: translate(-50%, -50%);
    margin: 0 auto;
}

.reels-tag-page #main {
    margin-top: 0px !important;
    margin-bottom: 0;
    overflow: visible !important;
    height: auto !important;
    min-height: auto !important;
}

.reels-tag-page #desktop-header {
    display: none;
}

.reels-tag-page #masterlayout {
    overflow: visible !important;
}

.reels-tag-page #nprogress .bar {
    display: none;
}
</style>
